import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ArrowLeft, ShieldCheck, Cpu, HeartPulse, Lock } from "lucide-react";
import { cn } from "../../lib/utils";

const TONE = (pct) => (pct >= 90 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444");

const FAMILY_ICON = {
  "SOC 2 (Service Organization Control 2)": ShieldCheck,
  "ISO (International Standards Organization)": Lock,
  "Other": HeartPulse,
};

function ProgressRing({ pct, size = 76, stroke = 7 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const color = TONE(pct);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="compliance-framework-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" className="text-muted-foreground/15" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[15px] font-bold" style={{ color }}>{pct}%</span>
      </div>
    </div>
  );
}

function FrameworkCard({ fw, onViewControls }) {
  return (
    <button
      type="button"
      onClick={() => onViewControls?.(fw.key)}
      className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all"
      data-testid={`compliance-framework-grid-card-${fw.key}`}
    >
      <div className="flex items-center gap-3">
        <ProgressRing pct={fw.maturity_pct} />
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold truncate">{fw.name}</div>
          <div className="text-[11.5px] text-muted-foreground">{fw.subtitle}</div>
          <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wide rounded-full border border-border px-2 py-0.5 text-muted-foreground">{fw.tag}</span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-border/60 text-[11.5px] space-y-0.5">
        {fw.basis === "audit" ? (
          <div className="text-muted-foreground">Latest audit: <span className="text-foreground font-medium">{fw.audit_stage}</span></div>
        ) : (
          <div className="text-muted-foreground">
            {fw.total_controls < fw.requirements ? (
              <><span className="text-foreground font-medium">{fw.total_controls}</span> of {fw.requirements} controls set up</>
            ) : (
              <><span className="text-foreground font-medium">{fw.total_controls}</span> controls mapped</>
            )}
          </div>
        )}
        <div className="text-muted-foreground">
          <span className="font-medium" style={{ color: TONE(fw.maturity_pct) }}>{fw.maturity_pct}% readiness</span>
          {fw.basis === "controls" && ` from ${fw.total_controls} set up controls`}
        </div>
      </div>
    </button>
  );
}

export default function ComplianceFrameworksGrid({ onBack, onViewControls }) {
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    api.get("/compliance/frameworks/grid").then((r) => setGroups(r.data.groups || [])).catch(() => setGroups([]));
  }, []);

  if (!groups) return <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-40 rounded-xl bg-muted/40 animate-pulse" />)}</div>;

  return (
    <div className="space-y-6" data-testid="compliance-frameworks-grid-page">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground" data-testid="compliance-frameworks-grid-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
      </button>
      {groups.map((group) => {
        const Icon = FAMILY_ICON[group.family] || ShieldCheck;
        return (
          <div key={group.family}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-[13px] font-semibold">{group.family}</h3>
              <span className="text-[11px] text-muted-foreground">({group.frameworks.length})</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.frameworks.map((fw) => (
                <FrameworkCard key={fw.key} fw={fw} onViewControls={onViewControls} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
